import { useState, useEffect, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  Activity,
  AlertCircle,
  BarChart3,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Gavel,
  Star,
  TrendingUp,
  Users,
} from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import { analyticsAPI, teamsAPI } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useOrg } from '../context/OrgContext'
import { Skeleton, StatCardSkeleton } from '../components/common/LoadingSkeleton'
import { getPeriodLabelAndRange } from '../utils/periodDateRange'
import { useAutoRefresh } from '../hooks'
import QuickAnalyticsHeader from '../components/analytics/QuickAnalyticsHeader'
import AnalyticsOverviewVideo from '../components/analytics/AnalyticsOverviewVideo'
import MeetingScorecardsSection from '../components/analytics/MeetingScorecardsSection'
import TrendChartCard from '../components/analytics/TrendChartCard'
import {
  agendaQualityMeetingScore,
  buildPerMeetingTrendSeries,
  efficiencyMeetingScore,
  productivityMeetingScore,
  timeSavedMeetingValue,
} from '../utils/analyticsTrendScores'

const ANALYTICS_TIMEFRAME_KEY = 'rithmo_analytics_timeframe'
const ANALYTICS_TEAM_KEY = 'rithmo_analytics_team_id'
const PERIOD_MAP = { week: '7d', month: '30d', year: '12m', all: 'all' }
const PERSONAL_SCOPE = '__personal__'
const ORG_SCOPE = '__org__'
const SHOW_OUTPUT_OPERATIONAL_INSIGHTS = false

function readStoredAnalyticsTimeframe() {
  try {
    const v = sessionStorage.getItem(ANALYTICS_TIMEFRAME_KEY)
    if (v === 'week' || v === 'month' || v === 'year' || v === 'all') return v
  } catch {
    // ignore
  }
  return 'month'
}

function AnalyticsPage() {
  const [activeTimeframe, setActiveTimeframeState] = useState(readStoredAnalyticsTimeframe)
  const [apiAnalytics, setApiAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [teams, setTeams] = useState([])
  // selectedScope: a team UUID, or '__org__' for org-wide (org admins only)
  const [selectedScope, setSelectedScopeState] = useState(null)
  const [teamsReady, setTeamsReady] = useState(false)

  const { isAuthenticated } = useAuth()
  const { isAdmin: isOrgAdmin } = useOrg()

  const setSelectedScope = useCallback((scope) => {
    setSelectedScopeState(scope)
    try {
      if (scope) sessionStorage.setItem(ANALYTICS_TEAM_KEY, scope)
      else sessionStorage.removeItem(ANALYTICS_TEAM_KEY)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      setTeams([])
      setSelectedScopeState(null)
      setTeamsReady(true)
      return
    }
    setTeamsReady(false)
    let cancelled = false
    teamsAPI.list().then((res) => {
      if (cancelled) return
      const payload = res?.data ?? res
      const raw = Array.isArray(payload?.teams) ? payload.teams : []
      const memberTeams = raw.filter((t) => t?.myRole != null)
      memberTeams.sort((a, b) =>
        String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' })
      )
      setTeams(memberTeams)
      setSelectedScopeState((prev) => {
        if (prev && (prev === ORG_SCOPE || prev === PERSONAL_SCOPE || memberTeams.some((t) => t.id === prev))) return prev
        try {
          const stored = sessionStorage.getItem(ANALYTICS_TEAM_KEY)
          if (stored === ORG_SCOPE) return ORG_SCOPE
          if (stored === PERSONAL_SCOPE) return PERSONAL_SCOPE
          if (stored && memberTeams.some((t) => t.id === stored)) return stored
        } catch { /* ignore */ }
        return PERSONAL_SCOPE
      })
    }).catch(() => {
      if (!cancelled) {
        setTeams([])
        setSelectedScopeState(null)
      }
    }).finally(() => {
      if (!cancelled) setTeamsReady(true)
    })
    return () => { cancelled = true }
  }, [isAuthenticated])

  const timeframeOptions = [
    { id: 'week', label: 'Week' },
    { id: 'month', label: 'Month' },
    { id: 'year', label: 'Year' },
    { id: 'all', label: 'All time' },
  ]

  const setActiveTimeframe = useCallback((id) => {
    setActiveTimeframeState(id)
    try {
      sessionStorage.setItem(ANALYTICS_TIMEFRAME_KEY, id)
    } catch {
      // ignore
    }
  }, [])

  const detailPeriodLabels = {
    week: 'Week avg',
    month: 'Month avg',
    year: 'Year avg',
    all: 'All-time avg',
  }

  const loadAnalytics = useCallback(() => {
    if (!teamsReady) return
    setLoading(true)
    const isPersonal = selectedScope === PERSONAL_SCOPE
    const teamId = selectedScope && selectedScope !== ORG_SCOPE && selectedScope !== PERSONAL_SCOPE ? selectedScope : null
    const scope = isPersonal ? 'personal' : null
    analyticsAPI.getDashboard(PERIOD_MAP[activeTimeframe] || '30d', teamId, null, scope).then((res) => {
      setApiAnalytics(res?.data ?? null)
      setLoading(false)
    }).catch(() => {
      setApiAnalytics(null)
      setLoading(false)
    })
  }, [activeTimeframe, selectedScope, teamsReady])

  useEffect(() => {
    const id = window.setTimeout(() => {
      loadAnalytics()
    }, 0)
    return () => window.clearTimeout(id)
  }, [loadAnalytics])

  useAutoRefresh(loadAnalytics, { intervalMs: 5 * 60 * 1000, staleTtlMs: 60 * 1000 })

  const period = PERIOD_MAP[activeTimeframe] || '30d'
  const { label: periodLabel, dateRangeText: periodDateRangeText } = getPeriodLabelAndRange(
    period,
    apiAnalytics?.label,
    apiAnalytics?.periodStart,
    apiAnalytics?.periodEnd
  )
  const analyticsLabel = periodDateRangeText
    ? `${periodLabel} (${periodDateRangeText})`
    : (apiAnalytics?.label ?? periodLabel)

  const quick = apiAnalytics?.quick ?? null
  // -- Commented out: decision follow-through (rate/completed/avgDaysToClose not yet implemented in backend) --
  // const decisionFollowThrough = apiAnalytics?.decisionFollowThrough ?? null

  const trendReferenceNowMs = apiAnalytics?.periodEnd
    ? new Date(apiAnalytics.periodEnd).getTime()
    : null

  const productivityTrendSeries = useMemo(() => {
    return buildPerMeetingTrendSeries({
      meetings: apiAnalytics?.recentMeetingAnalytics ?? [],
      chartRange: activeTimeframe,
      referenceNowMs: trendReferenceNowMs,
      scoreForMeeting: productivityMeetingScore,
    })
  }, [activeTimeframe, apiAnalytics?.recentMeetingAnalytics, trendReferenceNowMs])

  const efficiencyTrendSeries = useMemo(() => {
    return buildPerMeetingTrendSeries({
      meetings: apiAnalytics?.recentMeetingAnalytics ?? [],
      chartRange: activeTimeframe,
      referenceNowMs: trendReferenceNowMs,
      scoreForMeeting: efficiencyMeetingScore,
    })
  }, [activeTimeframe, apiAnalytics?.recentMeetingAnalytics, trendReferenceNowMs])

  const agendaQualityTrendSeries = useMemo(() => {
    return buildPerMeetingTrendSeries({
      meetings: apiAnalytics?.recentMeetingAnalytics ?? [],
      chartRange: activeTimeframe,
      referenceNowMs: trendReferenceNowMs,
      scoreForMeeting: agendaQualityMeetingScore,
    })
  }, [activeTimeframe, apiAnalytics?.recentMeetingAnalytics, trendReferenceNowMs])

  const timeSavedTrendSeries = useMemo(() => {
    return buildPerMeetingTrendSeries({
      meetings: apiAnalytics?.recentMeetingAnalytics ?? [],
      chartRange: activeTimeframe,
      referenceNowMs: trendReferenceNowMs,
      scoreForMeeting: timeSavedMeetingValue,
    })
  }, [activeTimeframe, apiAnalytics?.recentMeetingAnalytics, trendReferenceNowMs])

  const isTrendUp = (trend) => trend != null && !String(trend).startsWith('-')
  const hasTrend = (trend) => trend != null && String(trend).trim() !== '' && String(trend).trim() !== '0' && String(trend).trim() !== '0%'

  const formatMinutes = (minutes) => {
    const abs = Math.abs(minutes)
    const sign = minutes < 0 ? '-' : ''
    if (abs < 60) return `${sign}${abs}m`
    const hours = Math.floor(abs / 60)
    const mins = Math.round((abs % 60) * 10) / 10
    return mins ? `${sign}${hours}h ${mins}m` : `${sign}${hours}h`
  }

  const isPersonalScope = selectedScope === PERSONAL_SCOPE
  const contributionScore = apiAnalytics?.contributionScore ?? null
  const decisionContributions = contributionScore?.decisionContributions ?? null
  const actionItems = contributionScore?.actionItems ?? null
  const personalAgendaAdherence = contributionScore?.agendaAdherence ?? null

  const stats = quick ? [
    {
      label: 'Time Saved',
      value: formatMinutes(apiAnalytics?.timeSaved?.totalMinutes ?? 0),
      icon: <Clock className="w-5 h-5" />,
      trend: null,
      trendUp: false,
      valueColor: (apiAnalytics?.timeSaved?.totalMinutes ?? 0) > 0 ? 'text-emerald-600' : undefined,
    },
    ...(isPersonalScope ? [
      {
        label: 'Contribution Score',
        value: contributionScore?.avg != null ? contributionScore.avg.toFixed(1) : '—',
        icon: <Star className="w-5 h-5" />,
        trend: hasTrend(contributionScore?.trend) ? contributionScore.trend : null,
        trendUp: isTrendUp(contributionScore?.trend),
        subtitle: contributionScore?.meetingCount > 0
          ? `${contributionScore.meetingCount} meeting${contributionScore.meetingCount !== 1 ? 's' : ''} analyzed`
          : 'No data yet',
      },
    ] : []),
    ...(isPersonalScope ? [
      {
        label: 'Decision Contributions',
        value: decisionContributions != null ? decisionContributions.total : '—',
        icon: <Gavel className="w-5 h-5" />,
        trend: hasTrend(decisionContributions?.trend) ? decisionContributions.trend : null,
        trendUp: isTrendUp(decisionContributions?.trend),
      },
    ] : [
      {
        label: 'Avg Decisions',
        value: quick.decisionsAvg.toFixed(1),
        icon: <Gavel className="w-5 h-5" />,
        trend: quick.trends?.decisions ?? '0',
        trendUp: isTrendUp(quick.trends?.decisions),
      },
    ]),
    ...(isPersonalScope ? [
      {
        label: 'Action Items',
        value: actionItems != null ? actionItems.assigned : '—',
        icon: <ClipboardCheck className="w-5 h-5" />,
        trend: hasTrend(actionItems?.trend) ? actionItems.trend : null,
        trendUp: isTrendUp(actionItems?.trend),
        subtitle: actionItems != null
          ? actionItems.assigned > 0
            ? `${actionItems.completed} of ${actionItems.assigned} completed`
            : 'None assigned'
          : 'No data yet',
      },
    ] : [
      {
        label: 'Talk Equity Score',
        value: `${Math.round((quick.talkEquityAvg ?? 0) * 100)}%`,
        icon: <Users className="w-5 h-5" />,
        trend: quick.trends?.talkEquity ?? '0%',
        trendUp: isTrendUp(quick.trends?.talkEquity),
      },
    ]),
    {
      label: 'Agenda Adherence',
      value: isPersonalScope
        ? (personalAgendaAdherence?.score != null ? `${personalAgendaAdherence.score}%` : '—')
        : `${Math.round((quick.agendaAdherenceAvg ?? 0) * 100)}%`,
      icon: <CheckCircle className="w-5 h-5" />,
      trend: isPersonalScope
        ? (hasTrend(personalAgendaAdherence?.trend) ? personalAgendaAdherence.trend : null)
        : (quick.trends?.agenda ?? '0%'),
      trendUp: isPersonalScope
        ? isTrendUp(personalAgendaAdherence?.trend)
        : isTrendUp(quick.trends?.agenda),
      subtitle: isPersonalScope && personalAgendaAdherence != null && personalAgendaAdherence.itemCount === 0
        ? 'No attributed items yet'
        : undefined,
    },
    ...(!isPersonalScope ? [
      {
        label: 'Interruptions',
        value: quick.interruptions ?? 0,
        icon: <AlertCircle className="w-5 h-5" />,
        trend: quick.trends?.interruptions ?? '0',
        trendUp: isTrendUp(quick.trends?.interruptions),
        valueColor: (quick.interruptions ?? 0) === 0 ? 'text-emerald-600' : 'text-red-600',
      },
    ] : []),
  ] : []

  // Operational insights kept behind a flag for now so the cards can be restored quickly.
  const insights = []
  if (SHOW_OUTPUT_OPERATIONAL_INSIGHTS && apiAnalytics?.insights?.meetingOutputFollowThrough) {
    const v = apiAnalytics.insights.meetingOutputFollowThrough
    insights.push({
      id: 'meeting-output-follow-through',
      label: 'Meeting Output Follow-through',
      value: v.value ?? '—',
      detail: v.detail ?? '',
      icon: <CheckCircle className="w-5 h-5" />,
    })
  }
  if (SHOW_OUTPUT_OPERATIONAL_INSIGHTS && apiAnalytics?.insights?.avgTimeToCloseOutput) {
    const v = apiAnalytics.insights.avgTimeToCloseOutput
    insights.push({
      id: 'avg-time-to-close-output',
      label: 'Avg Time to Close Output',
      value: v.value ?? '—',
      detail: v.detail ?? '',
      icon: <Clock className="w-5 h-5" />,
    })
  }

  const meetingCount = apiAnalytics?.meetingCount ?? 0
  const hasData = meetingCount > 0

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-600">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
                  {hasData && <span className="badge-primary">{meetingCount} meeting{meetingCount !== 1 ? 's' : ''}</span>}
                </div>
                <p className="text-gray-500">
                  Operational insights across your monitored meetings.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-2">
              <div className="flex flex-wrap items-center justify-end gap-4">
                <AnalyticsOverviewVideo />
                <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1">
                  {timeframeOptions.map((option) => {
                    const isActive = activeTimeframe === option.id
                    return (
                      <button
                        key={option.id}
                        onClick={() => setActiveTimeframe(option.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          isActive
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <p className="text-sm text-gray-500">Showing {analyticsLabel}</p>
            </div>
          </div>

          {/* -- Commented out: team/department filter dropdowns (not connected to backend) --
          <div className="mt-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Team</span>
                <select
                  value={selectedTeam}
                  onChange={(event) => setSelectedTeam(event.target.value)}
                  className="bg-transparent text-sm text-gray-700 focus:outline-none"
                >
                  {teamOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Department</span>
                <select
                  value={selectedDepartment}
                  onChange={(event) => setSelectedDepartment(event.target.value)}
                  className="bg-transparent text-sm text-gray-700 focus:outline-none"
                >
                  {departmentOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              Filters apply to admin analytics only.
            </p>
          </div>
          */}
        </motion.div>

        {teamsReady && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <button
              onClick={() => setSelectedScope(PERSONAL_SCOPE)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                selectedScope === PERSONAL_SCOPE
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-primary-400 hover:text-primary-600'
              }`}
            >
              Personal
            </button>
            {isOrgAdmin && (
              <button
                onClick={() => setSelectedScope(ORG_SCOPE)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                  selectedScope === ORG_SCOPE
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                Organization
              </button>
            )}
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => setSelectedScope(team.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                  selectedScope === team.id
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                {team.name}
              </button>
            ))}
          </div>
        )}

        <OperatingPlanSummaryBanner plan={apiAnalytics?.operatingPlan ?? null} />

        {loading && (
          <div className="space-y-8">
            <div className="glass p-6">
              <Skeleton className="h-5 w-40 mb-2" />
              <Skeleton className="h-3 w-64 mb-6" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <StatCardSkeleton key={i} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="glass p-6 space-y-4">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-8 w-20" />
                  <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && !hasData && (
          <div className="text-center py-20 text-gray-400">
            <p className="text-lg font-medium">No meeting data available</p>
            <p className="text-sm mt-1">Complete some meetings to see your analytics.</p>
          </div>
        )}

        {!loading && hasData && (
          <div className="space-y-8">
            {/* Quick Analytics */}
            {stats.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="glass p-6"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
                  <QuickAnalyticsHeader visibility={apiAnalytics?.visibility} className="flex-1 min-w-0 pr-0 sm:pr-4" />
                  <p className="shrink-0 text-sm text-gray-400 sm:text-right sm:pt-0.5 w-full sm:w-auto">
                    Period: {analyticsLabel}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className="stat-card"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.valueColor ? (stat.valueColor === 'text-emerald-600' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600') : 'bg-primary-50 text-primary-600'}`}>
                          {stat.icon}
                        </div>
                        {stat.trend != null && stat.trend !== undefined && (
                          <span className={`text-sm font-medium ${stat.trendUp ? 'text-green-600' : 'text-red-600'}`}>
                            {stat.trend}
                          </span>
                        )}
                      </div>
                      <p className={`stat-value text-center ${stat.valueColor ?? ''}`}>{stat.value}</p>
                      <p className="stat-label text-center">{stat.label}</p>
                      {stat.subtitle && (
                        <p className="text-xs text-gray-400 text-center mt-1">{stat.subtitle}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            <MeetingScorecardsSection
              periodDashboard={apiAnalytics}
              loading={loading}
              analyticsLabel={analyticsLabel}
              detailPeriodLabel={detailPeriodLabels[activeTimeframe] ?? 'Period avg'}
              chartRange={activeTimeframe}
            />

            <div className="space-y-6">
              <TrendChartCard
                icon={TrendingUp}
                title="Productivity"
                description="Per-meeting productivity score based on decisions created and interruption control."
                series={productivityTrendSeries}
                loading={loading}
                ariaLabel="Productivity trend chart"
                transitionDelay={0.15}
                showRangeSelector={false}
              />

              <TrendChartCard
                icon={Activity}
                title="Efficiency"
                description="Per-meeting efficiency score based on schedule discipline and focused time."
                series={efficiencyTrendSeries}
                loading={loading}
                ariaLabel="Efficiency trend chart"
                transitionDelay={0.2}
                showRangeSelector={false}
              />

              <TrendChartCard
                icon={ClipboardCheck}
                title="Agenda Quality"
                description="Per-meeting agenda quality score based on structure, clarity, and outcome markers."
                series={agendaQualityTrendSeries}
                loading={loading}
                ariaLabel="Agenda quality trend chart"
                transitionDelay={0.25}
                showRangeSelector={false}
                emptyMessage="No agenda quality data for this range."
              />

              <TrendChartCard
                icon={Clock}
                title="Time Saved"
                description="Per-meeting minutes saved from interruption control, tangent avoidance, and ending on time."
                series={timeSavedTrendSeries}
                loading={loading}
                ariaLabel="Time saved trend chart"
                transitionDelay={0.3}
                showRangeSelector={false}
                emptyMessage="No time-saved data for this range."
                valueFormatter={(value) => formatMinutes(value)}
                yAxisTickFormatter={(value) => `${Math.round(value)}`}
                yAxisBounds={null}
              />
            </div>

            {/* -- Commented out: Decision Follow-through card (rate/completed/avgDaysToClose not yet implemented in backend) --
              {decisionFollowThrough && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="glass p-6"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Decision Follow-through</h3>
                      <p className="text-sm text-gray-500">Completed actions from prior decisions</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600">
                      <ClipboardCheck className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="flex items-end gap-3 mb-4">
                    <p className="text-4xl font-semibold text-gray-900">
                      {Math.round((decisionFollowThrough.rate ?? 0) * 100)}%
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                    <div>
                      <p className="text-gray-900 font-medium">{decisionFollowThrough.completed ?? 0}</p>
                      <p>Decisions completed</p>
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{decisionFollowThrough.pending ?? 0}</p>
                      <p>Still open</p>
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">
                        {decisionFollowThrough.avgDaysToClose != null
                          ? `${Number(decisionFollowThrough.avgDaysToClose).toFixed(1)} days`
                          : '—'}
                      </p>
                      <p>Avg time to close</p>
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{(decisionFollowThrough.decisionsPerMeeting ?? 0).toFixed(1)}</p>
                      <p>Decisions per meeting</p>
                    </div>
                  </div>
                </motion.div>
              )}
              */}

            {/* Operational Insights */}
            {insights.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Operational Insights</h2>
                    <p className="text-sm text-gray-500">
                      Signals across meetings and teams.
                    </p>
                  </div>
                  <p className="text-sm text-gray-400">Signals update with {analyticsLabel}</p>
                </div>
                <div className={`grid grid-cols-1 md:grid-cols-2 ${insights.length >= 4 ? 'xl:grid-cols-4' : `xl:grid-cols-${insights.length}`} gap-6`}>
                  {insights.map((insight) => (
                    <div key={insight.id} className="stat-card">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600">
                          {insight.icon}
                        </div>
                      </div>
                      <p className="stat-value">{insight.value}</p>
                      <p className="stat-label">{insight.label}</p>
                      <p className="text-sm text-gray-500 mt-2 leading-relaxed">{insight.detail}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

/**
 * Phase 3B — read-only operating-plan banner.
 *
 * Renders nothing when no active directive exists. Renders a small,
 * non-interactive summary card when one is present. The card explicitly
 * says the plan is "stored, not yet runtime-enforced" so the executive
 * never mistakes Phase 3B persistence for Phase 3C runtime influence.
 *
 * No backend recomputation: all fields come straight from the analytics
 * dashboard payload (analyticsController.loadOperatingPlanSummaryForDashboard).
 */
function OperatingPlanSummaryBanner({ plan }) {
  if (!plan) return null
  const goalLabel = (plan.goal || '').replace(/_/g, ' ')
  const postureLabel = (plan.posture || '').replace(/_/g, ' ')
  const maxPostureLabel = (plan.maxPosture || '').replace(/_/g, ' ')
  // Phase 3D — directive-vs-organic-learning influence line. The backend
  // attaches `influenceSummary` only when a team is in scope (analytics
  // dashboard `teamId` query param). Without a team we cannot compute
  // influence (audit_records are team-keyed); show "Influence mix pending"
  // instead of misleading zeros.
  const summary = plan.influenceSummary
  const hasAnyClassified = !!summary && (
    summary.directiveDrivenProposalCount > 0
    || summary.organicProposalCount > 0
    || summary.unknownProposalCount > 0
  )
  return (
    <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 lg:p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Operating plan
            </span>
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
              Stored — not yet runtime-enforced
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-700 leading-relaxed">
            {plan.executiveMessage}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            {summary == null
              ? 'Influence mix pending'
              : !hasAnyClassified
                ? `Influence mix pending — no learning activity yet (window: last ${summary.windowDays} day(s))`
                : `Directive influence: ${summary.directiveInfluenceSharePct}% · `
                  + `Organic learning: ${summary.organicLearningSharePct}% · `
                  + `Unknown: ${summary.unknownSharePct}% `
                  + `(window: last ${summary.windowDays} day(s))`}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm shrink-0">
          <div>
            <p className="text-xs text-gray-400">Goal</p>
            <p className="font-medium text-gray-800 capitalize">{goalLabel || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Posture</p>
            <p className="font-medium text-gray-800 capitalize">{postureLabel || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Cap</p>
            <p className="font-medium text-gray-800 capitalize">{maxPostureLabel || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Last change</p>
            <p className="font-medium text-gray-800">
              {plan.lastChangeAtMs
                ? new Date(plan.lastChangeAtMs).toLocaleDateString()
                : '—'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsPage
